import toArray from "lodash/toArray";

import { Fragment } from "react";

import { Flex, Stack } from "@/storybook-components";
import CheckBoxQuestion from "./checkbox-question";
import DateQuestion from "./date-question";
import DropDownQuestion from "./drop-down-question";
import EmailQuestion from "./email-question";
import EmojiQuestion from "./emoji-question";
import EssayQuestion from "./essay-question";
import FileQuestion from "./file-question";
import LikertQuestion from "./likert-question";
import NumberQuestion from "./number-question";
import RadioButtonQuestion from "./radio-button-question";
import RankingQuestion from "./ranking-question";
import RegionQuestion from "./region-question";
import SectionQuestion from "./section-question";
import SingleLineQuestion from "./single-line-question";
import { segregateQuestions, triggeredQuestions } from "@/helpers/survey";

import type { Question } from "@/types";
import { useAppSelector } from "@/redux/store/hooks";

const RenderQuestionList = ({ parentQuestions, conditionalQuestions }) => {
  const { answers } = useAppSelector((state) => state.survey);

  const questionNode = (question: Question) => {
    switch (question.type) {
      case "DateQuestion":
        return <DateQuestion question={question} />;
      case "FileQuestion":
        return <FileQuestion question={question} />;
      case "EmojiQuestion":
        return <EmojiQuestion question={question} />;
      case "EmailQuestion":
        return <EmailQuestion question={question} />;
      case "EssayQuestion":
        return <EssayQuestion question={question} />;
      case "NumberQuestion":
        return <NumberQuestion question={question} />;
      case "LikertQuestion":
        return <LikertQuestion question={question} />;
      case "RegionQuestion":
        return <RegionQuestion question={question} />;
      case "RankingQuestion":
        return <RankingQuestion question={question} />;
      case "SectionQuestion":
        return <SectionQuestion question={question} />;
      case "SingleLineQuestion":
        return <SingleLineQuestion question={question} />;
      case "CheckBoxQuestion":
        return <CheckBoxQuestion question={question} />;
      case "DropDownQuestion":
        return <DropDownQuestion question={question} />;
      case "RadioButtonQuestion":
        return <RadioButtonQuestion question={question} />;
      default:
        return null;
    }
  };

  const findTriggeredQuestions = (
    parent: Question,
    children: Array<Question>
  ) => {
    // Check if question has been answered
    if (!answers[parent.id]) return null;

    const parentQuestions = triggeredQuestions(answers[parent.id], children);

    const props = {
      parentQuestions,
      conditionalQuestions
    };

    return <RenderQuestionList {...props} />;
  };

  return parentQuestions.map((question: Question) => (
    <Fragment key={`ehq-survey-${question.id}`}>
      {question.type === "SectionQuestion" ? (
        questionNode(question)
      ) : (
        <Flex>{questionNode(question)}</Flex>
      )}
      {!!conditionalQuestions[question.id] &&
        findTriggeredQuestions(question, conditionalQuestions[question.id])}
    </Fragment>
  ));
};

const QuestionList = (): JSX.Element => {
  const { pages, questions, options } = useAppSelector((state) => state.survey);

  const currentPage = pages.find((page) => page.current);
  const questionsInPage = toArray(questions).filter(
    (q) => q.pageId === currentPage.id
  );
  const optionsInPage = toArray(options).filter((o) =>
    questionsInPage.some((q) => q.id === o.questionId)
  );

  const { parentQuestions, conditionalQuestions } = segregateQuestions(
    questionsInPage,
    optionsInPage
  );

  const state = {
    conditionalQuestions,
    parentQuestions: parentQuestions.filter((q) => !q.skip)
  };

  return (
    <Stack spacing={12}>
      <RenderQuestionList {...state} />
    </Stack>
  );
};

export default QuestionList;
